'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  toasterApi,
  type ExportStatus,
  downloadUrlAsFile,
} from '@terra/ui-design-system';
import { PDF_FINDINGS_EXPORT_PROGRESS_SSE_EVENT } from '@terra/shared-contracts-pdf-generator';
import { getAuthHeaders } from '../utils/authHeaders';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { useSSE } from '../app/providers/SseProvider';

type UseFindingsPdfExportOptions = {
  applicationId?: string;
};

type UseFindingsPdfExportReturn = {
  exportFindingsPdf: (
    findingIds: string[],
    options?: { includeRemediation?: boolean },
  ) => Promise<void>;
  isExporting: boolean;
  exportStatus: ExportStatus;
  progress: number;
  error: string | null;
  reset: () => void;
};

export function useFindingsPdfExport(
  options: UseFindingsPdfExportOptions = {},
): UseFindingsPdfExportReturn {
  const { subscribe } = useSSE();
  const { applicationId } = options;
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const reset = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setIsExporting(false);
    setExportStatus('idle');
    setProgress(0);
    setError(null);
  }, []);

  const exportFindingsPdf = useCallback(
    async (
      findingIds: string[],
      options?: { includeRemediation?: boolean },
    ) => {
      if (findingIds.length === 0) {
        toasterApi.error({
          title: 'No findings selected',
          description: 'Please select at least one finding to export.',
        });

        return;
      }

      setIsExporting(true);
      setExportStatus('preparing');
      setProgress(10);
      setError(null);

      try {
        const jobId =
          typeof crypto !== 'undefined' &&
          typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `pdf-findings-export-${Date.now()}`;

        unsubscribeRef.current?.();
        unsubscribeRef.current = subscribe(
          PDF_FINDINGS_EXPORT_PROGRESS_SSE_EVENT,
          (sseData) => {
            if (sseData.jobId !== jobId) {
              return;
            }

            if (sseData.status === 'FAILED') {
              console.error('PDF export failed:', sseData.error);
              setError('Failed to export PDF. Please try again.');
              setExportStatus('failed');
              setIsExporting(false);

              return;
            }

            if (sseData.status === 'PREPARING') {
              setExportStatus('preparing');
            } else if (sseData.status === 'RENDERING') {
              setExportStatus('generating');
            }

            if (typeof sseData.progress === 'number') {
              setProgress(sseData.progress);
            }
          },
        );

        const authHeaders = await getAuthHeaders();
        const isElectron =
          typeof window !== 'undefined' &&
          window.electronAPI?.isElectronEnvironment;

        // In Electron, getSessionToken() returns null for OpenID because auth is handled
        // via main process header injection. Don't throw an error in this case.
        if (!authHeaders.Authorization && !isElectron) {
          throw new Error('Authentication required');
        }

        const gatewayUrl = getCurrentGatewayUrl();

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          ...authHeaders,
        };

        setExportStatus('generating');
        setProgress(40);

        const generateResponse = await fetch(
          `${gatewayUrl}/pdf-generator/download/findings`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              findingIds,
              applicationId,
              jobId,
              options: {
                includeScreenshots: true,
                includeRemediation: options?.includeRemediation ?? false,
              },
            }),
          },
        );

        if (!generateResponse.ok) {
          const errorData = await generateResponse.json().catch(() => ({}));

          throw new Error(
            errorData.error ||
              `Failed to generate PDF: ${generateResponse.status}`,
          );
        }

        setProgress(80);

        setExportStatus('downloading');
        const pdfBlob = await generateResponse.blob();

        setProgress(90);

        const url = URL.createObjectURL(pdfBlob);

        downloadUrlAsFile({
          url,
          filename: `findings-export-${new Date().toISOString().split('T')[0]}.pdf`,
        });
        URL.revokeObjectURL(url);

        setProgress(100);
        setExportStatus('completed');
        setIsExporting(false);
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;

        toasterApi.success({
          title: 'PDF exported successfully',
          description: `${findingIds.length} finding${findingIds.length !== 1 ? 's' : ''} exported to PDF.`,
        });
      } catch (err) {
        console.error('PDF export failed:', err);
        const errorMessage = 'Failed to export PDF';

        setError(errorMessage);
        setExportStatus('failed');
        setIsExporting(false);
        unsubscribeRef.current?.();
        unsubscribeRef.current = null;
        toasterApi.error({
          title: 'PDF export failed',
          description: errorMessage,
        });
      }
    },
    [applicationId, subscribe],
  );

  useEffect(() => {
    return () => {
      unsubscribeRef.current?.();
    };
  }, []);

  return {
    exportFindingsPdf,
    isExporting,
    exportStatus,
    progress,
    error,
    reset,
  };
}
